// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll, Instance as TippyInstance } from "tippy.js";

import FancyScroll from "@ces/App/Components/Common/FancyScroll";
import LazyTippy from "@ces/App/Components/Common/LazyTippy";
import xHeaderUpdate from "@ces/App/Handlers/XNG/Header";
import { constants } from "@ces/XDL/constants";
import {
    addScrollbarGutter,
    color,
    ellipsis,
    removeScrollbarGutter,
    styledScrollbar,
    xClassNames,
} from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    // XNG-Change
    viewAllLink?: LinkPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: tippy trigger prop */
        tippyTrigger?: string;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
        /** XNG-Changes */
        headingLink?: { url: string; title?: string };
    }
>;

// XNG-Changes
const mapDispatchToProps = {
    xHeaderUpdate,
};

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    ResolveThunks<typeof mapDispatchToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.div<MenuStyleProps>`
    background: ${color("common.white")};
    width: ${getProp("width", 175)}px;
    max-width: 25vw;
    height: 100%;
    max-height: calc(calc(100vh - ${constants.header.size.height}px) * 0.8);
    padding: 6px 10px 0 5px;
    list-style: none;
    margin: 0;
    overflow: auto;
    outline: 1px solid ${getColor("common.border")};

    border-radius: 0 0 2px 2px;
    &:has(div > [aria-expanded="true"]) {
        border-bottom-right-radius: 0;
        & > div > [aria-expanded="true"] + [data-tippy-root] .MenuStyle {
            border-bottom-left-radius: 0 !important;
        }
    }

    ${styledScrollbar({ margined: 17 })}
    transition: unset;
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;
// XNG-Change
const MenuWrapper = styled.nav<MenuWrapperProps>`
    &[aria-expanded="true"] {
        .trigger-item {
            position: relative;
            ${TypographyStyle} {
                color: ${color("primary")};
            }
            ${IconWrapper} {
                rotate: 180deg;
                color: ${color("text.link")};
            }
            &:after {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                bottom: -12px;
                width: 100%;
                height: 4px;
                border-radius: 2px;
                display: block;
                background: ${color("primary")};
            }
        }
    }

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean; weight?: number }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: ${({ weight }) => weight};
    letter-spacing: 0;
    line-height: 22px;
    ${ellipsis(2)}
`;

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "text.link",
    },
    active: {
        background: "primary",
        text: "common.white",
        icon: "common.white",
    },
};

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 2px;
    & > a {
        padding: 8px;
    }

    & + [data-tippy-root] {
        &,
        & > div {
            height: 100%;
        }
    }

    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: inline-block;
        width: calc(100% - 15px);
    }

    ${IconWrapper} {
        color: ${color(menuItemColorStyle.default.icon)};
        width: 24px;
        height: 14px;
        top: 50%;
        transform: translateY(-50%);
        padding-right: 8px;
    }

    &:focus,
    &:focus-within,
    &:hover,
    &[aria-expanded="true"] {
        background: ${color(menuItemColorStyle.active.background)};
        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }

    ${injectCss}
`;

/**
 * XNG-Changes
 */
const DELAY = {
    long: 120,
    short: 80,
};
const hideOnPopperBlurPlugin = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: TippyInstance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    setTimeout(() => {
                        if (
                            event.relatedTarget &&
                            event.relatedTarget instanceof HTMLElement &&
                            !instance.popper.contains(event.relatedTarget)
                        ) {
                            instance.hide();
                        }
                    }, DELAY.long);
                });
            },
        };
    },
};

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

const DEFAULT_MENU_WIDTH = 294;

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: DEFAULT_MENU_WIDTH,
        tippyTrigger: "mouseenter focusin",
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, mergeCss, headingLink, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;

                    const viewAllLink = headingLink ? (
                        <Link {...applyProp("viewAllLink")} href={headingLink.url}>
                            {headingLink.title || translate("View All Product Categories")}
                        </Link>
                    ) : null;

                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        // XNG-Changes
                        const parentMenu = React.createRef<HTMLDivElement>();

                        return (
                            <FancyScroll refElement={parentMenu} contentPaddingRight={0}>
                                <MenuStyle
                                    width={otherProps.width}
                                    ref={parentMenu}
                                    className="MenuStyle"
                                    data-depth={currentDepth}
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                >
                                    {currentDepth === 1 && viewAllLink}
                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            /**
                                             * XNG-Changes
                                             */
                                            const maxDepth = item.maxDepth || parentDepth;

                                            const hasChildren =
                                                item.children?.some(o => !o.excludeFromNavigation) &&
                                                currentDepth < maxDepth;

                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }

                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={applyStyledProp(
                                                        ["cssOverrides", "menuItem"],
                                                        resolvedMergeCss,
                                                    )}
                                                    key={`${item.title}_${index}`}
                                                    className={xClassNames.append([
                                                        "MenuItem",
                                                        hasChildren ? "has-children" : "",
                                                    ])}
                                                    data-test-selector="menuItem"
                                                    data-depth={currentDepth}
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(hasChildren);
                                                    }}
                                                >
                                                    <Clickable
                                                        // xng-change
                                                        href={
                                                            hasChildren ? item.url : `${item.url}?stockedItemsOnly=true`
                                                        }
                                                        onClick={() => {
                                                            this.closeMenu();
                                                        }}
                                                        {...applyProp("menuItemClickableProps")}
                                                    >
                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            weight="normal"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                </MenuItemStyle>
                                            );
                                            return (
                                                <StyledWrapper key={`${item.title}_${index}`}>
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <LazyTippy
                                                            render={() =>
                                                                renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                )
                                                            }
                                                            trigger="mouseenter"
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={[0, -1]}
                                                            delay={[0, 0.5]} // Stop flickering
                                                            interactiveBorder={24}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlurPlugin]}
                                                            getReferenceClientRect={() => {
                                                                if (
                                                                    !parentMenu.current ||
                                                                    typeof parentMenu.current[
                                                                        "getBoundingClientRect"
                                                                    ] === "undefined"
                                                                ) {
                                                                    return new DOMRect(0, 0, 0, 0);
                                                                }
                                                                return parentMenu.current!.getBoundingClientRect();
                                                            }}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </StyledWrapper>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };

                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <LazyTippy
                                render={() => (
                                    <StyledWrapper
                                        css={css`
                                            width: ${otherProps.width || DEFAULT_MENU_WIDTH}px;
                                        `}
                                    >
                                        {renderMenuItems(otherProps.maxDepth || 0, menuItems, 1)}
                                    </StyledWrapper>
                                )}
                                trigger={this.props.tippyTrigger}
                                placement="bottom-start"
                                hideOnClick={this.props.hideOnClick}
                                interactive={true}
                                animation={false}
                                offset={[0, 13]}
                                delay={[50, 20]} // Stop flickering
                                interactiveBorder={24}
                                plugins={[hideOnPopperBlurPlugin]}
                                touch={["hold", 250]}
                                appendTo="parent"
                                onShow={instance => {
                                    hideAll(); // To close all Tippy instances
                                    this.props.xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_menu` });
                                    addScrollbarGutter();
                                    ariaAppHider.hide(document.body);
                                }}
                                onHidden={instance => {
                                    setTimeout(() => {
                                        this.props.xHeaderUpdate({
                                            key: "unregisterMenu",
                                            value: `${instance.id}_menu`,
                                        });
                                        removeScrollbarGutter();
                                        ariaAppHider.show(document.body);
                                    }, DELAY.short);
                                }}
                                popperOptions={{
                                    modifiers: [
                                        {
                                            name: "arrow",
                                            options: {
                                                padding: 0,
                                                element: ".menu-arrow",
                                            },
                                        },
                                    ],
                                }}
                            >
                                <MenuWrapper
                                    css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                    {...{ "aria-labelledby": descriptionId }}
                                >
                                    {React.cloneElement(menuTrigger, {
                                        id: descriptionId,
                                        className: `trigger trigger-item`,
                                    })}
                                </MenuWrapper>
                            </LazyTippy>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(null, mapDispatchToProps)(withTheme(Menu));
export { MenuItemText };
